/**
 * Playlist player — port of app/templates/playlists/player.html, same
 * API contract and the same Mixpanel events ('Playlist Viewed' surface
 * 'player', 'Playlist Clip Played' per clip) so dashboards keep reading.
 *
 * TWO PLAYBACK MODES per clip, decided by the data: a pre-cut 10s MP4
 * (clip_url) plays whole and 'ended' advances; otherwise the full match
 * video seeks a window around the moment and timeupdate watches for the
 * clip's end. Advance honors the auto-advance toggle either way.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { trackEvent } from '../../lib/analytics'
import { ApiError, api } from '../../lib/api'
import { NO_FRAME_RATE_NOTE } from '../../lib/frameRate'
import { frameToVideoTime } from '../../lib/viz/frameTime'
import {
  type GameMeta, type PlaylistItem, type PlaylistPayload,
  clockFor, titleFor, toSyncState,
} from './shared'
import './playlists.css'

const PRE_SECONDS = 3
const POST_SECONDS = 7

/** play() returns undefined in jsdom and in pre-promise browsers —
 *  a bare .catch() there is a synchronous TypeError that kills the
 *  whole clip load. */
function safePlay(v: HTMLVideoElement): void {
  const p = v.play() as Promise<void> | undefined
  p?.catch(() => { /* autoplay blocked; the controls remain */ })
}

export default function PlaylistPlayer() {
  const { playlistId } = useParams<{ playlistId: string }>()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const started = useRef(false)
  const [name, setName] = useState('')
  const [items, setItems] = useState<PlaylistItem[]>([])
  const [games, setGames] = useState<Record<string, GameMeta>>({})
  const [idx, setIdx] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const autoAdvance = useRef(true)
  const clipEnd = useRef<number | null>(null)
  const video = useRef<HTMLVideoElement | null>(null)
  const strip = useRef<HTMLDivElement | null>(null)
  const touch = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    started.current = false // a new reel auto-starts again
    api<PlaylistPayload>(`/api/v1/playlists/${playlistId}`)
      .then((d) => {
        // Fired on load SUCCESS, not on mount: the Flask page redirected
        // an unauthorized viewer before its script could run, so a 403
        // never counted as a view — the React page must not either.
        trackEvent('Playlist Viewed', { playlist_id: playlistId, surface: 'player' })
        setName(d.playlist.name)
        setItems(d.items)
        setGames(d.games || {})
        setLoaded(true)
      })
      .catch((e: unknown) => {
        // Same contract as the Flask page and the editor: a reel you
        // cannot see sends you back to the list, not to an error.
        if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
          void navigate('/clip-room')
          return
        }
        setError(e instanceof ApiError ? e.message : String(e))
      })
  }, [playlistId, navigate])

  const playItem = useCallback((i: number, itemList?: PlaylistItem[],
                                gameMap?: Record<string, GameMeta>) => {
    const list = itemList ?? items
    const gmap = gameMap ?? games
    if (!list.length) return
    const next = Math.max(0, Math.min(i, list.length - 1))
    setIdx(next)
    const item = list[next]
    if (!item) return
    const g = gmap[item.game_code]
    trackEvent('Playlist Clip Played', {
      playlist_id: playlistId,
      game_code: item.game_code,
      clip_index: next,
      clip_count: list.length,
      moment_type: item.moment_type || null,
    })
    const v = video.current
    if (!v) return
    if (item.clip_url) {
      // Pre-cut 10s MP4: play the whole file, 'ended' advances.
      clipEnd.current = null
      if (v.src !== item.clip_url) v.src = item.clip_url
      v.currentTime = 0
      safePlay(v) // autoplay may be blocked; controls remain
    } else if (g?.video_url) {
      // Full-video seek window around the moment; the canonical
      // frame→time math (lib/viz/frameTime) handles video-clock frames.
      const sync = toSyncState(g)
      if (!sync) {
        // No frame rate recorded: the moment cannot be found in the
        // video, so nothing is seeked (the note under the player says why).
        clipEnd.current = null
        return
      }
      const fps = sync.fps
      const startF = item.start_frame ?? item.frame - PRE_SECONDS * fps
      const endF = item.end_frame ?? item.frame + POST_SECONDS * fps
      const startT = frameToVideoTime(sync, Math.max(0, startF))
      clipEnd.current = frameToVideoTime(sync, endF)
      // Identity by ABSOLUTE URL, not basename: the Jinja player's
      // basename test confused two games whose R2 keys end in the same
      // uploaded filename and seeked into the previous game's video.
      const abs = new URL(g.video_url, window.location.href).href
      if (v.src !== abs) v.src = abs
      const seekAndPlay = () => {
        v.currentTime = startT
        safePlay(v) // autoplay may be blocked
      }
      if (v.readyState >= 1) seekAndPlay()
      else v.addEventListener('loadedmetadata', seekAndPlay, { once: true })
    } else {
      clipEnd.current = null
    }
  }, [items, games, playlistId])

  // First clip once the payload lands; ?start=<index> deep-links from the
  // editor's per-item play buttons.
  useEffect(() => {
    if (!loaded || !items.length || started.current) return
    started.current = true
    const start = parseInt(search.get('start') || '0', 10)
    playItem(Number.isNaN(start) ? 0 : start, items, games)
  }, [loaded, items, games, search, playItem])

  const idxRef = useRef(idx)
  idxRef.current = idx
  const advance = useCallback(() => {
    const v = video.current
    if (!autoAdvance.current) { v?.pause(); return }
    if (idxRef.current < items.length - 1) playItem(idxRef.current + 1)
    else v?.pause()
  }, [items.length, playItem])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return
      const v = video.current
      if (e.code === 'Space') {
        e.preventDefault()
        if (v) { if (v.paused) safePlay(v); else v.pause() }
      }
      if (e.key === 'ArrowRight') playItem(idxRef.current + 1)
      if (e.key === 'ArrowLeft') playItem(idxRef.current - 1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [playItem])

  useEffect(() => {
    // Keep the current chip in view as clips advance.
    const chip = strip.current?.children[idx]
    // Optional call: jsdom has no scrollIntoView, and a missing scroll
    // is cosmetic — it must never take the clip change down with it.
    if (chip && typeof chip.scrollIntoView === 'function') {
      chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
    }
  }, [idx])

  if (error) return <div className="plp-body"><div className="plp-page"><p className="plp-empty">{error}</p></div></div>

  const item = items[idx]
  const g = item ? games[item.game_code] : undefined

  return (
    <div className="plp-body">
      <div className="plp-page">
        <div className="plp-top">
          <Link to={`/playlists/${playlistId}`}>&larr; Back</Link>
          <h1>{name}</h1>
          {items.length > 0 && (
            <span className="plp-n">clip {idx + 1} of {items.length}</span>
          )}
          <div className="plp-auto">
            <label>
              <input type="checkbox" defaultChecked
                     onChange={(e) => { autoAdvance.current = e.target.checked }} />
              {' '}Auto-advance
            </label>
          </div>
        </div>

        {loaded && items.length === 0 ? (
          <div className="plp-empty">This playlist has no clips yet.</div>
        ) : (
          <>
            <div className="plp-video-wrap"
                 onTouchStart={(e) => {
                   const t = e.touches[0]
                   if (t) touch.current = { x: t.clientX, y: t.clientY }
                 }}
                 onTouchEnd={(e) => {
                   const t = e.changedTouches[0]
                   if (!touch.current || !t) return
                   const dx = t.clientX - touch.current.x
                   const dy = t.clientY - touch.current.y
                   touch.current = null
                   if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 2) {
                     playItem(dx < 0 ? idxRef.current + 1 : idxRef.current - 1)
                   }
                 }}>
              <video ref={video} controls playsInline
                     onEnded={advance}
                     onTimeUpdate={() => {
                       const v = video.current
                       if (v && clipEnd.current != null && v.currentTime >= clipEnd.current) {
                         advance()
                       }
                     }} />
              {item && (
                <div className="plp-clock">
                  {item.game_code} &middot; {clockFor(item, g)}
                </div>
              )}
              {item && !item.clip_url && g?.video_url && toSyncState(g) === null && (
                <div className="plp-note" role="note">{NO_FRAME_RATE_NOTE}</div>
              )}
              {item?.note && <div className="plp-note">&ldquo;{item.note}&rdquo;</div>}
            </div>
            <div className="plp-strip" ref={strip}>
              {items.map((it, i) => (
                <button key={it.id}
                        className={`plp-chip${i === idx ? ' current' : ''}`}
                        onClick={() => playItem(i)}>
                  <div className="th">&#9654;</div>
                  <div className="cap">
                    <div className="t">{titleFor(it)}</div>
                    <div className="m">
                      {it.game_code} &middot; {clockFor(it, games[it.game_code])}
                      {it.score != null ? ` · ${Math.round(it.score * 100)}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="plp-keys">
              <b>Space</b> play/pause &middot; <b>&larr;/&rarr;</b> prev/next clip
            </div>
          </>
        )}
      </div>
    </div>
  )
}
